import { api } from "@shared/lib/api";
import { BackednResponse } from "@shared/lib/types/response";
import { IPagination } from "@shared/lib/types/user";
import {
  IAIWorkspace,
  IAIWorkspaceRequest,
  IAIWorkspaceResponse,
  ICreateAIWorkspacePayload,
  ICreateCreatorFitRequestPayload,
  IAddCreatorsToFitRequestPayload,
  IAddCreatorsToScriptGenerateRequestPayload,
  ICreateScriptGenerateRequestPayload,
  IConceptualiseCreatorScriptPayload,
  ICreatorScriptGeneratorJobDetail,
  ISaveCreatorScriptPayload,
  ICreateDraftVetRequestPayload,
  ICreateScriptVetRequestPayload,
  IGenerateMasterPromptPayload,
  IGenerateMasterPromptResult,
  IRunVetComplianceDraftPayload,
  IRunVetComplianceScriptPayload,
  ISearchAIWorkspacePayload,
  IUpdateAIWorkspacePayload,
  IUpdateCreatorFitMasterPromptPayload,
  IUpdateScriptGenerationMasterPromptPayload,
  IUpdateVetRequestPromptPayload
} from "../types";

export class AiWorkspaceService {
  public static async createAIWorkspace(payload: ICreateAIWorkspacePayload) {
    return (await api
      .post("ai-workspace", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceResponse>;
  }

  public static async searchAIWorkspace(payload: ISearchAIWorkspacePayload) {
    return (await api
      .post("ai-workspace/search", {
        json: payload,
      })
      .json()) as BackednResponse<{
      data: IAIWorkspace[];
      pagination: IPagination;
    }>;
  }

  public static async getAIWorkspace(aiWorkspaceId: string) {
    return (await api
      .get(`ai-workspace`, {
        searchParams: { aiWorkspaceId },
      })
      .json()) as BackednResponse<IAIWorkspace>;
  }

  public static async getAIWorkspaceRequests(aiWorkspaceId: string) {
    return (await api
      .get(`ai-workspace/request/by-workspace`, {
        searchParams: { aiWorkspaceId },
      })
      .json()) as BackednResponse<IAIWorkspaceRequest[]>;
  }

  public static async getAIWorkspaceRequest(aiWorkspaceRequestId: string) {
    return (await api
      .get(`ai-workspace/request`, {
        searchParams: { aiWorkspaceRequestId },
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async createCreatorFitRequest(
    payload: ICreateCreatorFitRequestPayload
  ) {
    return (await api
      .post(`ai-workspace/request/creator-fit`, {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async addCreatorsToFitRequest(
    payload: IAddCreatorsToFitRequestPayload
  ) {
    return (await api
      .post(`ai-workspace/request/creator-fit/add-creators`, {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async createScriptGenerateRequest(
    payload: ICreateScriptGenerateRequestPayload
  ) {
    return (await api
      .post(`ai-workspace/request/script-generate`, {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async addCreatorsToScriptGenerateRequest(
    payload: IAddCreatorsToScriptGenerateRequestPayload
  ) {
    return (await api
      .post(`ai-workspace/request/script-generate/add-creators`, {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async getCreatorScriptGeneratorJob(creatorScriptGeneratorJobId: string) {
    return (await api
      .get(`ai-tools/creator-script-generator/job`, {
        searchParams: { creatorScriptGeneratorJobId },
      })
      .json()) as BackednResponse<ICreatorScriptGeneratorJobDetail>;
  }

  public static async conceptualiseCreatorScript(payload: IConceptualiseCreatorScriptPayload) {
    return (await api
      .post(`ai-tools/creator-script-generator/conceptualise`, {
        json: payload,
      })
      .json()) as BackednResponse<ICreatorScriptGeneratorJobDetail>;
  }

  public static async saveCreatorScript(payload: ISaveCreatorScriptPayload) {
    return (await api
      .post(`ai-tools/creator-script-generator/save-script`, {
        json: payload,
      })
      .json()) as BackednResponse<ICreatorScriptGeneratorJobDetail>;
  }

  public static async reanalyzeCreatorFitCheck(payload: {
    aiWorkspaceRequestId: string;
    creatorFitCheckId: string;
  }) {
    return (await api
      .post(`ai-workspace/request/creator-fit/re-analyze`, {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async updateAIWorkspace(payload: IUpdateAIWorkspacePayload) {
    return (await api
      .put("ai-workspace", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspace>;
  }

  public static async updateCreatorFitMasterPrompt(
    payload: IUpdateCreatorFitMasterPromptPayload
  ) {
    return (await api
      .put("ai-workspace/creator-fit-master-prompt", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspace>;
  }
  public static async updateScriptingRequestMasterPrompt(
    payload: IUpdateCreatorFitMasterPromptPayload
  ) {
    return (await api
      .put("ai-workspace/script-vet-master-prompt", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspace>;
  }
  public static async updateDraftVetMasterPrompt(
    payload: IUpdateCreatorFitMasterPromptPayload
  ) {
    return (await api
      .put("ai-workspace/draft-vet-master-prompt", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspace>;
  }

  public static async updateScriptGenerationMasterPrompt(
    payload: IUpdateScriptGenerationMasterPromptPayload
  ) {
    return (await api
      .put("ai-workspace/script-generation-master-prompt", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspace>;
  }

  public static async createScriptVetRequest(
    payload: ICreateScriptVetRequestPayload
  ) {
    return (await api
      .post("ai-workspace/request/script-vet", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async createDraftVetRequest(
    payload: ICreateDraftVetRequestPayload
  ) {
    return (await api
      .post("ai-workspace/request/draft-vet", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async updateVetRequestPrompt(
    payload: IUpdateVetRequestPromptPayload
  ) {
    return (await api
      .put("ai-workspace/request/master-prompt", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async runScriptVetCompliance(
    payload: IRunVetComplianceScriptPayload
  ) {
    return (await api
      .post("ai-workspace/request/script-vet/compliance-check", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }

  public static async generateMasterPrompt(
    payload: IGenerateMasterPromptPayload
  ) {
    return (await api
      .post("ai-workspace/generate-master-prompt", {
        json: payload,
      })
      .json()) as BackednResponse<IGenerateMasterPromptResult>;
  }

  public static async runDraftVetCompliance(
    payload: IRunVetComplianceDraftPayload
  ) {
    return (await api
      .post("ai-workspace/request/draft-vet/compliance-check", {
        json: payload,
      })
      .json()) as BackednResponse<IAIWorkspaceRequest>;
  }
}
